import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Tabs, Modal, Button, Spin, message } from "antd";
import {
  DownloadOutlined,
  CheckSquareOutlined,
  UserOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import { useSelector } from "react-redux";
import { attachment } from "@iso/services/attachment.service";
import { commonHelper } from "@iso/lib/helpers/commonHelper";
import LibraryTab from "@iso/components/FileBrowser/LibraryTab";
import UploadTab from "@iso/components/FileBrowser/UploadTab";
import { getFileIcon, getFileName } from "@iso/components/FileBrowser/fileUtils";
import { isSuccess } from "@iso/lib/helpers/apiResponse";
import FileManagerWrapper from "../FileManager/FileManager.styles";
import { SDSModalGlobalStyles } from "./SDS.styles";
import useCanManageSDSFiles from "./useCanManageSDSFiles";
import SDSAssignUserModal from "./SDSAssignUserModal";
import SDSSetExpiryModal from "./SDSSetExpiryModal";
import ShareFileModal from "../FileManager/ShareFileModal";

const { TabPane } = Tabs;

const SDS = () => {
  const activePropertyId = useSelector(
    (state) => state.property.activePropertyId
  );
  // When the active property belongs to a corporate group, the SDS page
  // becomes a corp-wide library: a single "Safety Data Sheets" folder
  // shared across every property in the group. Files / folder ops use
  // corporate_group_id instead of property_id end-to-end.
  //
  // The full active-property object lives in localStorage (not Redux —
  // state.property.properties is unused by most of the app, see Sidebar /
  // Sensors / Asset). Re-derive whenever activePropertyId changes so
  // switching property in another tab updates corporateGroupId here too.
  const cachedCorporateGroupId = useMemo(() => {
    const active = commonHelper.getActiveProperty();
    if (!active || active.id !== activePropertyId) return null;
    return active.corporate_group_id || null;
  }, [activePropertyId]);
  // Effective scope, separate from the cached value: the loader can
  // demote to property scope if ensureSdsFolder rejects the corporate
  // request (e.g. the corporate group was removed since active_property
  // was last cached). Re-syncs to the cached value whenever the active
  // property changes.
  const [corporateGroupId, setCorporateGroupId] = useState(cachedCorporateGroupId);
  useEffect(() => {
    setCorporateGroupId(cachedCorporateGroupId);
  }, [cachedCorporateGroupId]);
  const isCorporate = Boolean(corporateGroupId);
  const pageTitle = isCorporate
    ? "Safety Data Sheets — Corporate Library"
    : "Safety Data Sheets";
  const tabLabel = isCorporate ? "Corporate Library" : "Library";
  const canManage = useCanManageSDSFiles();
  const [sdsFolderId, setSdsFolderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentFolderId, setCurrentFolderId] = useState(null);

  // Bulk-select state. SDS is single-scope (property), so just one ids list.
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);

  // Preview modal state
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // Assign / expiry modal state. `targetFiles` is the file(s) the modal acts
  // on — either a single file (per-card icon click) or the current bulk
  // selection.
  const [assignUserVisible, setAssignUserVisible] = useState(false);
  const [setExpiryVisible, setSetExpiryVisible] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [targetFiles, setTargetFiles] = useState([]);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (!activePropertyId) {
      setSdsFolderId(null);
      setCurrentFolderId(null);
      setLoading(false);
      setLoadError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    attachment
      .ensureSdsFolder(activePropertyId, corporateGroupId)
      .then((res) => {
        if (cancelled) return;
        if (isSuccess(res) && res.folder) {
          setSdsFolderId(res.folder.id);
          setCurrentFolderId(res.folder.id);
          setLoading(false);
          return;
        }
        // Stale corporate context (e.g. the corp group was removed from
        // the property since active_property was last cached). Demote to
        // property scope and let the effect re-run — keep loading=true
        // through the transition so the UI doesn't flash an error state.
        if (corporateGroupId) {
          setCorporateGroupId(null);
          return;
        }
        setLoadError(res?.message || "Could not load Safety Data Sheets folder.");
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("ensureSdsFolder failed", err);
        if (corporateGroupId) {
          setCorporateGroupId(null);
          return;
        }
        setLoadError("Could not load Safety Data Sheets folder.");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activePropertyId, corporateGroupId]);

  // Drop bulk selections when scope changes or bulk mode toggles — ids
  // from one scope (property or corp group) are not valid in another, and
  // toggling Done should clear pending choices.
  useEffect(() => {
    setBulkSelectedIds([]);
  }, [activePropertyId, corporateGroupId, bulkMode]);

  const handleUploadSuccess = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const refresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleFileAction = useCallback(
    async (action, file) => {
      switch (action) {
        case "preview":
          setPreviewFile(file);
          setPreviewVisible(true);
          break;
        case "download":
          if (file.full_path) {
            try {
              const response = await fetch(file.full_path);
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = getFileName(file);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            } catch (err) {
              console.error("SDS download blob failed, falling back", err);
              window.open(file.full_path, "_blank");
            }
          }
          break;
        case "delete":
          Modal.confirm({
            title: "Delete File",
            content: `Are you sure you want to delete "${getFileName(file)}"? This action cannot be undone.`,
            okText: "Delete",
            okType: "danger",
            onOk: async () => {
              try {
                const res = await attachment.destroy(file.id);
                if (isSuccess(res)) {
                  message.success("File deleted");
                  refresh();
                } else {
                  message.error(res?.message || "Failed to delete file");
                }
              } catch (err) {
                console.error("SDS delete failed", err);
                message.error("Failed to delete file");
              }
            },
          });
          break;
        case "assignUser":
          setTargetFiles([file]);
          setAssignUserVisible(true);
          break;
        case "setExpiry":
          setTargetFiles([file]);
          setSetExpiryVisible(true);
          break;
        case "share":
          setShareTarget(file);
          setShareModalVisible(true);
          break;
        default:
          break;
      }
    },
    [refresh]
  );

  const closeAssignUser = useCallback(() => {
    setAssignUserVisible(false);
    setTargetFiles([]);
  }, []);

  const closeSetExpiry = useCallback(() => {
    setSetExpiryVisible(false);
    setTargetFiles([]);
  }, []);

  const submitAssignUser = useCallback(
    async (userId) => {
      setModalSubmitting(true);
      try {
        const ids = targetFiles.map((f) => f.id);
        if (ids.length === 1) {
          const res = await attachment.updateAttachment(ids[0], {
            user_id: userId,
          });
          if (isSuccess(res)) {
            message.success("User assigned");
          } else {
            message.error(res?.message || "Failed to assign user");
          }
        } else {
          const res = await attachment.bulkUpdate({
            ids,
            ...(isCorporate
              ? { corporate_group_id: corporateGroupId }
              : { property_id: activePropertyId }),
            user_id: userId,
          });
          if (isSuccess(res)) {
            message.success(`User assigned to ${ids.length} files`);
            setBulkSelectedIds([]);
          } else {
            message.error(res?.message || "Failed to assign user");
          }
        }
        closeAssignUser();
        refresh();
      } catch (err) {
        console.error("SDS submitAssignUser failed", err);
        message.error("Failed to assign user");
      } finally {
        setModalSubmitting(false);
      }
    },
    [targetFiles, activePropertyId, corporateGroupId, isCorporate, closeAssignUser, refresh]
  );

  const submitSetExpiry = useCallback(
    async (expiryDate) => {
      setModalSubmitting(true);
      try {
        const ids = targetFiles.map((f) => f.id);
        if (ids.length === 1) {
          const res = await attachment.updateAttachment(ids[0], {
            expiry_date: expiryDate,
          });
          if (isSuccess(res)) {
            message.success("Expiry updated");
          } else {
            message.error(res?.message || "Failed to update expiry");
          }
        } else {
          const res = await attachment.bulkUpdate({
            ids,
            ...(isCorporate
              ? { corporate_group_id: corporateGroupId }
              : { property_id: activePropertyId }),
            expiry_date: expiryDate,
          });
          if (isSuccess(res)) {
            message.success(`Expiry updated on ${ids.length} files`);
            setBulkSelectedIds([]);
          } else {
            message.error(res?.message || "Failed to update expiry");
          }
        }
        closeSetExpiry();
        refresh();
      } catch (err) {
        console.error("SDS submitSetExpiry failed", err);
        message.error("Failed to update expiry");
      } finally {
        setModalSubmitting(false);
      }
    },
    [targetFiles, activePropertyId, corporateGroupId, isCorporate, closeSetExpiry, refresh]
  );

  const handleBulkAssignUser = useCallback(() => {
    setTargetFiles(bulkSelectedIds.map((id) => ({ id })));
    setAssignUserVisible(true);
  }, [bulkSelectedIds]);

  const handleBulkSetExpiry = useCallback(() => {
    setTargetFiles(bulkSelectedIds.map((id) => ({ id })));
    setSetExpiryVisible(true);
  }, [bulkSelectedIds]);

  const handleBulkDownload = useCallback(async () => {
    if (!bulkSelectedIds.length) return;
    setBulkBusy(true);
    try {
      const data = await attachment.bulkDownload({
        ids: bulkSelectedIds,
        ...(isCorporate
          ? { corporate_group_id: corporateGroupId }
          : { property_id: activePropertyId }),
      });
      if (!data) {
        message.error("Failed to download files");
        return;
      }
      const blob = new Blob([data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `sds-${new Date()
        .toISOString()
        .replace(/[-:T]/g, "")
        .slice(0, 14)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success("Download started");
    } catch (err) {
      console.error("SDS bulk download failed", err);
      message.error("Failed to download files");
    } finally {
      setBulkBusy(false);
    }
  }, [bulkSelectedIds, activePropertyId, corporateGroupId, isCorporate]);

  const handleBulkDelete = useCallback(() => {
    if (!bulkSelectedIds.length) return;
    Modal.confirm({
      title: `Delete ${bulkSelectedIds.length} files?`,
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        setBulkBusy(true);
        try {
          const res = await attachment.bulkDelete({
            ids: bulkSelectedIds,
            ...(isCorporate
              ? { corporate_group_id: corporateGroupId }
              : { property_id: activePropertyId }),
          });
          if (isSuccess(res)) {
            message.success(
              `Deleted ${res.data?.deleted_count ?? bulkSelectedIds.length} files`
            );
            setBulkSelectedIds([]);
            refresh();
          } else {
            message.error(res?.message || "Failed to delete files");
          }
        } catch (err) {
          console.error("SDS bulk delete failed", err);
          message.error("Failed to delete files");
        } finally {
          setBulkBusy(false);
        }
      },
    });
  }, [bulkSelectedIds, activePropertyId, corporateGroupId, isCorporate, refresh]);

  const renderPreviewContent = () => {
    if (!previewFile) return null;
    const name = getFileName(previewFile);

    if (previewFile.type === "image" && previewFile.full_path) {
      return (
        <div style={{ textAlign: "center" }}>
          <img
            src={previewFile.full_path}
            alt={name}
            style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: 4, cursor: "pointer" }}
            onClick={() => window.open(previewFile.full_path, "_blank", "noopener,noreferrer")}
          />
        </div>
      );
    }

    if (previewFile.type === "video" && previewFile.full_path) {
      return (
        <video controls src={previewFile.full_path} style={{ width: "100%" }} />
      );
    }

    if (previewFile.type === "audio" && previewFile.full_path) {
      return (
        <audio controls src={previewFile.full_path} style={{ width: "100%" }} />
      );
    }

    if (previewFile.type === "pdf" && previewFile.full_path) {
      return (
        <iframe
          src={previewFile.full_path}
          title={name}
          style={{ width: "100%", height: "70vh", border: "none" }}
        />
      );
    }

    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{ fontSize: 64, marginBottom: 16, color: "#999" }}>
          {getFileIcon(previewFile.type)}
        </div>
        <p style={{ fontSize: 16, color: "#333", marginBottom: 4 }}>{name}</p>
        <p style={{ fontSize: 13, color: "#999", textTransform: "uppercase", marginBottom: 20 }}>
          {previewFile.type}
        </p>
        {previewFile.full_path && (
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => handleFileAction("download", previewFile)}
          >
            Download
          </Button>
        )}
      </div>
    );
  };

  const renderToolbar = () => (
    <div className="file-manager-toolbar">
      <Button
        type={bulkMode ? "primary" : "default"}
        icon={<CheckSquareOutlined />}
        onClick={() => setBulkMode((prev) => !prev)}
      >
        {bulkMode ? "Done" : "Select multiple"}
      </Button>
    </div>
  );

  const renderBulkActionBar = () => {
    if (!bulkMode || bulkSelectedIds.length === 0) return null;
    return (
      <div className="bulk-action-bar">
        <span className="bulk-count">{bulkSelectedIds.length} selected</span>
        {canManage && (
          <Button
            icon={<UserOutlined />}
            disabled={bulkBusy}
            onClick={handleBulkAssignUser}
          >
            Assign user
          </Button>
        )}
        {canManage && (
          <Button
            icon={<ClockCircleOutlined />}
            disabled={bulkBusy}
            onClick={handleBulkSetExpiry}
          >
            Set expiry
          </Button>
        )}
        <Button
          icon={<DownloadOutlined />}
          loading={bulkBusy}
          onClick={handleBulkDownload}
        >
          Download
        </Button>
        <Button
          danger
          icon={<DeleteOutlined />}
          disabled={bulkBusy}
          onClick={handleBulkDelete}
        >
          Delete
        </Button>
        <Button
          type="text"
          style={{ color: "#fff", marginLeft: "auto" }}
          onClick={() => setBulkSelectedIds([])}
        >
          Clear
        </Button>
      </div>
    );
  };

  if (!activePropertyId) {
    return (
      <LayoutWrapper>
        <FileManagerWrapper>
          <Box id="containerBox">
            <div className="manage-property-header">
              <h2 className="manage-property-title" style={{ margin: 0 }}>
                {pageTitle}
              </h2>
            </div>
            <div style={{ padding: "40px 24px", textAlign: "center", color: "#999" }}>
              Please select a property first.
            </div>
          </Box>
        </FileManagerWrapper>
      </LayoutWrapper>
    );
  }

  if (loading) {
    return (
      <LayoutWrapper>
        <FileManagerWrapper>
          <Box id="containerBox">
            <div className="manage-property-header">
              <h2 className="manage-property-title" style={{ margin: 0 }}>
                {pageTitle}
              </h2>
            </div>
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <Spin />
            </div>
          </Box>
        </FileManagerWrapper>
      </LayoutWrapper>
    );
  }

  if (!sdsFolderId) {
    return (
      <LayoutWrapper>
        <FileManagerWrapper>
          <Box id="containerBox">
            <div className="manage-property-header">
              <h2 className="manage-property-title" style={{ margin: 0 }}>
                {pageTitle}
              </h2>
            </div>
            <div style={{ padding: "40px 24px", textAlign: "center", color: "#999" }}>
              {loadError || "Safety Data Sheets folder is unavailable for this property."}
            </div>
          </Box>
        </FileManagerWrapper>
      </LayoutWrapper>
    );
  }

  // Look up the assigned user / expiry of the single-file target so the
  // modal pickers preselect the current value.
  //
  // PR #358 review S5: bulk-mode hands the modals synthetic { id } objects
  // (the LibraryTab's full file cache is not exposed to this component), so
  // the chip data isn't available even when exactly one file is selected.
  // Skip preselection in bulk mode rather than render a misleading empty
  // picker as if the value were unset — users editing a single file in bulk
  // mode can still apply the new value, they just don't see the existing
  // one. The per-card icon path (`!bulkMode`) carries the full file object
  // and continues to preselect correctly.
  const singleTargetFile =
    !bulkMode && targetFiles.length === 1 ? targetFiles[0] : null;
  const initialAssignedUserId = singleTargetFile?.user?.id ?? null;
  const initialExpiry = singleTargetFile?.expiry_date ?? null;

  return (
    <LayoutWrapper>
      <SDSModalGlobalStyles />
      <FileManagerWrapper>
        <Box id="containerBox">
          <div className="manage-property-header">
            <h2 className="manage-property-title" style={{ margin: 0 }}>
              {pageTitle}
            </h2>
          </div>
          <div className="file-manager-content">
            <Tabs defaultActiveKey="library" size="small">
              <TabPane tab={tabLabel} key="library">
                {renderToolbar()}
                <LibraryTab
                  selectable={false}
                  onFileAction={handleFileAction}
                  refreshKey={refreshKey}
                  propertyId={isCorporate ? null : activePropertyId}
                  corporateGroupId={corporateGroupId}
                  onFolderChange={setCurrentFolderId}
                  rootFolderId={sdsFolderId}
                  bulkMode={bulkMode}
                  bulkSelectedIds={bulkSelectedIds}
                  onBulkSelectionChange={setBulkSelectedIds}
                  canManage={canManage}
                />
                {renderBulkActionBar()}
              </TabPane>
              <TabPane tab="Upload" key="upload">
                <UploadTab
                  onUploadSuccess={handleUploadSuccess}
                  currentFolderId={currentFolderId}
                  corporateGroupId={corporateGroupId}
                />
              </TabPane>
            </Tabs>
          </div>
        </Box>
      </FileManagerWrapper>

      {/* File Preview Modal */}
      <Modal
        visible={previewVisible}
        title={previewFile ? getFileName(previewFile) : "Preview"}
        footer={null}
        onCancel={() => {
          setPreviewVisible(false);
          setPreviewFile(null);
        }}
        width={800}
        destroyOnClose
      >
        {renderPreviewContent()}
      </Modal>

      <SDSAssignUserModal
        visible={assignUserVisible}
        propertyId={activePropertyId}
        initialUserId={initialAssignedUserId}
        fileCount={targetFiles.length}
        confirmLoading={modalSubmitting}
        onCancel={closeAssignUser}
        onConfirm={submitAssignUser}
      />

      <SDSSetExpiryModal
        visible={setExpiryVisible}
        initialExpiry={initialExpiry}
        fileCount={targetFiles.length}
        confirmLoading={modalSubmitting}
        onCancel={closeSetExpiry}
        onConfirm={submitSetExpiry}
      />

      <ShareFileModal
        visible={shareModalVisible}
        existingFile={shareTarget}
        propertyId={isCorporate ? null : activePropertyId}
        corporateGroupId={isCorporate ? corporateGroupId : null}
        onCancel={() => {
          setShareModalVisible(false);
          setShareTarget(null);
        }}
        onSuccess={() => {
          setShareModalVisible(false);
          setShareTarget(null);
        }}
      />
    </LayoutWrapper>
  );
};

export default SDS;
